import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Ticket, Video, Timer, MapPin, Wifi } from "lucide-react";
import { SequentialLeadFlow, nameStep } from "./lead/SequentialLeadFlow";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IST_OFFSET_MS = 5.5 * 3600 * 1000;
const hm = (s) => s.split(":").map(Number);

const nextSession = (sessions, now = new Date()) => {
  const ist = new Date(now.getTime() + IST_OFFSET_MS);
  let best = null;
  for (const s of sessions) {
    const [sh, sm] = hm(s.start), [eh, em] = hm(s.end);
    for (let d = 0; d < 8; d++) {
      const day = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate() + d));
      if (day.getUTCDay() !== s.dow) continue;
      const start = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), sh, sm) - IST_OFFSET_MS;
      const end = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), eh, em) - IST_OFFSET_MS;
      if (end <= now.getTime()) continue;
      if (!best || start < best.start) best = { ...s, start, end };
      break;
    }
  }
  return best;
};

const Countdown = ({ sessions }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const s = nextSession(sessions, new Date(now));
  if (!s) return null;
  const live = now >= s.start;
  const diff = Math.max(0, s.start - now);
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000), sec = Math.floor((diff % 60000) / 1000);
  const label = `${s.day} ${s.time.split(" – ")[0]} (${s.mode})`;
  const Cell = ({ v, l }) => (
    <div className="rounded-lg bg-black/40 border border-amber-400/30 px-2.5 py-1.5 min-w-[54px] text-center">
      <div className="text-xl font-black text-amber-300 tabular-nums leading-none">{String(v).padStart(2, "0")}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-black mt-0.5">{l}</div>
    </div>
  );
  return (
    <div data-testid="workshop-countdown" className="flex flex-wrap items-center gap-3 rounded-2xl bg-gradient-to-r from-amber-400/15 to-transparent border border-amber-400/30 px-4 py-3">
      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-300"><Timer className="w-4 h-4" /> {live ? "Live now" : "Next session"}</div>
      {live ? (
        <span data-testid="workshop-live-now" className="inline-flex items-center gap-2 text-sm font-black text-white"><span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> {label} · in progress</span>
      ) : (
        <div className="flex items-center gap-1.5" data-testid="workshop-countdown-cells">
          {d > 0 && <Cell v={d} l="days" />}<Cell v={h} l="hrs" /><Cell v={m} l="min" /><Cell v={sec} l="sec" />
          <span className="ml-2 text-xs font-bold text-slate-300">to {label} IST</span>
        </div>
      )}
    </div>
  );
};

export const WorkshopBanner = ({ compact = false }) => {
  const [ws, setWs] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/site/workshop`).then((r) => setWs(r.data)).catch(() => {}); }, []);
  if (!ws) return null;

  const sessLabel = (s) => `${s.day} ${s.mode} · ${s.time}`;
  const steps = [
    { key: "class", label: "Class", question: "Which class is the student in?", type: "choice", options: [{ value: "9th", label: "Class 9" }, { value: "10th", label: "Class 10" }, { value: "11th", label: "Class 11" }] },
    nameStep(),
    { key: "session", label: "Session", question: "Which session will you join?", type: "choice", options: [...ws.sessions.map((s) => ({ value: s.id, label: `${s.day} · ${s.mode}` })), { value: "both", label: "Both sessions" }] },
  ];
  const message = (a, labelOf) => {
    const chosen = a.session === "both" ? ws.sessions : ws.sessions.filter((s) => s.id === a.session);
    return `Hi Rankers Academy! Please register me for the *free ${ws.title}* (${ws.subtitle}) by ${ws.mentor}.\n\n` +
      `Student: ${a.name}\nClass: ${labelOf(steps[0], a.class)}\nSession: ${chosen.map(sessLabel).join(" + ")}\n\n` +
      `Please send my pass and the join link / venue details. Thank you!`;
  };

  return (
    <section id="workshop" data-testid="workshop-banner" className="relative overflow-hidden rounded-[28px] bg-[#0B0F1A] text-white shadow-2xl border border-amber-400/30">
      <div className="absolute inset-0 bg-[radial-gradient(800px_300px_at_90%_-10%,rgba(255,193,7,0.18),transparent_60%)]" />
      <div className={`relative grid ${compact ? "lg:grid-cols-[260px_1fr]" : "lg:grid-cols-[360px_1fr]"} gap-6 p-5 sm:p-8`}>
        <figure data-testid="workshop-lecturer-card" className="relative mx-auto lg:mx-0 w-full max-w-[360px] rounded-2xl overflow-hidden ring-2 ring-amber-400/70 shadow-[0_20px_60px_-20px_rgba(255,193,7,0.7)] bg-[#0B0F1A] self-start">
          <img src={compact ? ws.poster_sm_url : ws.poster_url} alt={`${ws.mentor} — lecturer`} width="720" height="899" loading="lazy" decoding="async" className="w-full h-auto block" data-testid="workshop-lecturer-photo" />
          <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0F1A] via-[#0B0F1A]/85 to-transparent pt-12 pb-4 px-4">
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">Lectured by</p>
            <p data-testid="workshop-lecturer-name" className="text-xl font-black text-white leading-tight mt-0.5">{ws.mentor}</p>
            <p className="text-[11px] text-slate-300 font-semibold mt-0.5">{ws.mentor_title}</p>
          </figcaption>
        </figure>

        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="workshop-pass-label" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-[#0B0F1A] text-[10px] font-black uppercase tracking-[0.18em]"><Ticket className="w-3.5 h-3.5" /> {ws.pass_label}</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] font-black uppercase tracking-[0.18em] text-amber-300"><Video className="w-3.5 h-3.5" /> {ws.for_class}</span>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-black">You are invited to our</p>
            <h2 data-testid="workshop-title" className="text-3xl sm:text-5xl font-black leading-[0.95] tracking-tight mt-1">
              {ws.heading_prefix} <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">{ws.subject}</span>
            </h2>
            <p className="text-sm sm:text-base font-bold text-slate-200 mt-2 tracking-wide">{ws.subtitle} · <span className="text-amber-300">{ws.price}</span></p>
            <p className="text-xs text-slate-400 mt-1">by <strong className="text-white">{ws.mentor}</strong> — {ws.mentor_title}</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {ws.topics.map((t) => (
              <div key={t.n} className="rounded-xl bg-white/5 border border-white/10 p-3 text-center" data-testid={`workshop-topic-${t.n}`}>
                <span className="inline-flex w-7 h-7 rounded-full border border-amber-400 text-amber-300 text-[11px] font-black items-center justify-center">{t.n}</span>
                <p className="text-xs font-black mt-1.5 leading-tight">{t.title}</p>
                <p className="text-[10px] text-slate-400">{t.line}</p>
              </div>
            ))}
          </div>

          <Countdown sessions={ws.sessions} />

          <div className="grid sm:grid-cols-2 gap-2">
            {ws.sessions.map((s) => (
              <div key={s.id} data-testid={`workshop-session-${s.id}`} className="flex items-center gap-3 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3">
                <CalendarDays className="w-5 h-5 text-amber-300" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-sky-300 font-black">{s.day}</p>
                  <p className="text-lg font-black text-amber-300 leading-tight">{s.time}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${s.mode === "Online" ? "bg-sky-400/20 text-sky-300" : "bg-emerald-400/20 text-emerald-300"}`} data-testid={`workshop-session-mode-${s.id}`}>
                  {s.mode === "Online" ? <Wifi className="w-3 h-3" /> : <MapPin className="w-3 h-3" />} {s.mode}
                </span>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="workshop-form">
            <SequentialLeadFlow testid="workshop-flow" dark steps={steps} intro="Claim your Free Trial Sessions Pass — sit in, then decide." title="Confirm your free workshop pass" ctaLabel="Get my pass on WhatsApp" buildMessage={message} />
          </div>
        </div>
      </div>
    </section>
  );
};
